"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MuTxtState = void 0;
const ui_1 = require("@jsonjoy.com/ui");
const keyboard_1 = require("@jsonjoy.com/keyboard");
const behavior_1 = require("../behavior");
const path_info_1 = require("../behavior/path-info");
const outline_1 = require("../behavior/outline");
const index_1 = require("../util/index");
const watch_1 = require("../util/watch");
const keyboard_2 = require("../behavior/keyboard");
const imagePaste_1 = require("../behavior/imagePaste");
const MuTxtApi_1 = require("./MuTxtApi");
const collaborative_slate_1 = require("@jsonjoy.com/collaborative-slate");
const toSlate_1 = require("@jsonjoy.com/collaborative-slate/lib/sync/toSlate");
const PeritextBinding_1 = require("@jsonjoy.com/collaborative-peritext/lib/PeritextBinding");
const slate_1 = require("slate");
const rsync_1 = require("@jsonjoy.com/ui/lib/utils/rsync");
const Sizer_1 = require("@jsonjoy.com/ui/lib/5-block/Sizer");
const windowSize_1 = require("@jsonjoy.com/ui/lib/utils/windowSize");
const ScrollArea_1 = require("@jsonjoy.com/ui/lib/4-card/ScrollArea");
const InlineState_1 = require("../inline/InlineState");
const BlockState_1 = require("../block/BlockState");
const VoidState_1 = require("../void/VoidState");
const OmniState_1 = require("../omni/OmniState");
const SelectAllGuardState_1 = require("../guard/SelectAllGuardState");
const DocumentMenu_1 = require("./DocumentMenu");
const IndicatorState_1 = require("./IndicatorState");
const ThingStore_1 = require("./ThingStore");
const CustomStyleState_1 = require("../custom-style/CustomStyleState");
const MuTxtTranslit_1 = require("../translit/MuTxtTranslit");
const json_crdt_1 = require("json-joy/lib/json-crdt");
const json_crdt_extensions_1 = require("json-joy/lib/json-crdt-extensions");
const font_1 = require("../behavior/font");
const isEditableWidth = (v) => v === 'narrow' || v === 'mid' || v === 'wide';
const isThemeOverride = (v) => v === 'auto' || v === 'light' || v === 'dark';
const createEmptyDocument = () => [{ type: 'p', children: [{ text: '' }] }];
const normalizeDocument = (value) => value && value.length ? value : createEmptyDocument();
class MuTxtState {
    constructor(editor, obj, opts) {
        this.editor = editor;
        this.obj = obj;
        this.api = new MuTxtApi_1.MuTxtApi(this);
        this.scroll = new ScrollArea_1.ScrollState({
            railWidth: 12,
            hideDelay: 5000,
        });
        this.version = ui_1.rsync.val(0);
        this.contentVersion = ui_1.rsync.val(0);
        this.scrollVersion = ui_1.rsync.val(0);
        this.editableBox = new rsync_1.ElBox();
        /**
         * Measures the outer Paper shell. Differs from {@link sizer}.width in
         * `fullwindow` and `fullscreen` modes, where the shell escapes the inline
         * layout (fixed-position 100vw, or native browser fullscreen at screen
         * size) — `sizer.width` would still report the inline container's width.
         */
        this.shellBox = new rsync_1.ElBox();
        this.wnd = (0, windowSize_1.windowSize)();
        this.kbd = new keyboard_1.KeyContext(undefined, 'mutxt');
        /** Current cursor position (caret or range). */
        this.cursor = ui_1.rsync.val(null);
        /** Caret selection. */
        this.caret = ui_1.rsync.val(null);
        /** Range selection. */
        this.selection = ui_1.rsync.val(null);
        this.focused = ui_1.rsync.val(false);
        this.readOnly = ui_1.rsync.val(false);
        this.blockLabel = ui_1.rsync.val('Paragraph');
        this.caretPath = ui_1.rsync.val([]);
        this.caretLinkHref = ui_1.rsync.val('');
        this.caretEmbedUrl = ui_1.rsync.val('');
        this.caretCodeText = ui_1.rsync.val('');
        this.caretMathThingId = ui_1.rsync.val('');
        this.alignment = ui_1.rsync.val('left');
        this.wordCount = ui_1.rsync.val(0);
        this.characterCount = ui_1.rsync.val(0);
        this.selectionText = ui_1.rsync.val('');
        this.inline = new InlineState_1.InlineState(this, this.scroll);
        this.block = new BlockState_1.BlockState(this, this.scroll);
        this.voids = new VoidState_1.VoidState(this);
        this.omni = new OmniState_1.OmniState(this);
        this.selectAllGuard = new SelectAllGuardState_1.SelectAllGuardState(this);
        this.indicator = new IndicatorState_1.IndicatorState(this);
        this.docMenu = new DocumentMenu_1.DocumentMenu(this);
        this.things = new ThingStore_1.ThingStore(this);
        this.customStyle = new CustomStyleState_1.CustomStyleState(this);
        /** Whether the keyboard-shortcuts modal is open. */
        this.shortcutsOpen = ui_1.rsync.val(false);
        /** Whether the developer "embed this editor" docs modal is open. */
        this.embedDocsOpen = ui_1.rsync.val(false);
        /** Current rendering display mode. */
        this.displayMode = ui_1.rsync.val('inline');
        /** Document-level typeface family. */
        this.font = ui_1.rsync.val('sans');
        /** Editable content area width preset. */
        this.editableWidth = ui_1.rsync.val('mid');
        /** MuTxt-internal theme override. When unset, the UiProvider theme applies. */
        this.theme = ui_1.rsync.val(undefined);
        /** Whether the OS/browser currently reports a dark color-scheme preference. */
        this.systemDark = ui_1.rsync.val(typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
            : false);
        /** The shell element wrapping the entire editor (header, content, footer). */
        this.shellEl = null;
        this.onTitleSubmit = void 0;
        this.bindKbdSource = (el) => {
            this.kbdSourceUnbind?.();
            this.kbdSourceUnbind = undefined;
            if (el) {
                const source = new keyboard_1.KeySourceEl(el);
                this.kbdSourceUnbind = source.bind(this.kbd);
            }
        };
        this.setFocused = (focused) => {
            this.focused.set(focused);
        };
        this.setReadOnly = (readOnly) => {
            this.readOnly.set(readOnly);
        };
        this.setFont = (kind) => {
            if (this.font.value === kind)
                return;
            if (this.readOnly.value)
                return;
            this.font.set(kind);
            this.obj.add('/font', kind);
        };
        this.setEditableWidth = (kind) => {
            if (this.editableWidth.value === kind)
                return;
            if (this.readOnly.value)
                return;
            this.editableWidth.set(kind);
            this.obj.add('/ew', kind);
        };
        this.setTheme = (value) => {
            if (this.theme.value === value)
                return;
            if (this.readOnly.value)
                return;
            this.theme.set(value);
            if (value === undefined)
                this.obj.del(['theme']);
            else
                this.obj.add('/theme', value);
        };
        this.setDisplayMode = (mode) => {
            const current = this.displayMode.value;
            if (current === mode)
                return;
            if (current === 'fullscreen' && document.fullscreenElement)
                document.exitFullscreen().catch(() => { });
            if (mode === 'fullscreen') {
                const target = this.shellEl ?? document.documentElement;
                target.requestFullscreen?.().catch(() => { });
            }
            this.displayMode.set(mode);
        };
        this.bindShell = (el) => {
            this.shellEl = el;
            this.shellBox.setEl(el ?? void 0);
        };
        this.sync = (contentChanged) => {
            const { version, contentVersion, editor, cursor, selection } = this;
            version.next(version.value + 1);
            if (contentChanged)
                contentVersion.next(contentVersion.value + 1);
            const { selection: editorSelection } = editor;
            let nextSelection = null;
            let nextCaret = null;
            if (editorSelection) {
                const isCollapsed = slate_1.Range.isCollapsed(editorSelection);
                if (!isCollapsed)
                    nextSelection = editorSelection;
                else
                    nextCaret = editorSelection;
            }
            if (editorSelection !== cursor.value)
                cursor.next(editorSelection);
            if (nextSelection !== selection.value)
                selection.next(nextSelection);
            if (nextCaret !== this.caret.value)
                this.caret.next(nextCaret);
            const text = (0, index_1.getEditorPlainText)(editor);
            const caret = (0, path_info_1.getCaretPathInfo)(editor);
            this.wordCount.set((0, index_1.getWordCount)(text));
            this.characterCount.set(text.length);
            this.blockLabel.set(this.api.blockLabel());
            this.caretPath.set(caret.path);
            this.caretLinkHref.set(caret.linkHref ?? '');
            this.caretEmbedUrl.set(caret.embedUrl ?? '');
            this.caretCodeText.set(caret.codeText ?? '');
            this.caretMathThingId.set(caret.mathThingId ?? '');
            this.alignment.set((0, behavior_1.getActiveAlignment)(editor));
            this.selectionText.set((0, index_1.getSelectedText)(editor));
            this.publishPresence?.();
        };
        this.onChange = () => {
            this.sync(true);
        };
        this.onSelection = () => {
            this.sync(false);
        };
        // --------------------------------------------------------- Document outline
        /** Cached document outline, recomputed when content changes. */
        this._outlineVersion = -1;
        this._outlineCache = [];
        /**
         * Returns the document heading outline. The result is cached per
         * `contentVersion`, so repeated calls within the same content revision are
         * cheap. Consumers that want re-render reactivity should call this from a
         * component that subscribes to `contentVersion`.
         */
        this.outline = () => {
            const version = this.contentVersion.value;
            if (this._outlineVersion === version)
                return this._outlineCache;
            this._outlineCache = (0, outline_1.getDocumentOutline)(this.editor.children);
            this._outlineVersion = version;
            return this._outlineCache;
        };
        this.readOnly.next(!!opts?.readOnly);
        this.translit = opts?.translit ?? new MuTxtTranslit_1.MuTxtTranslit();
        this.translit.bindState(this);
        if (obj.read('/@type') !== 'mutxt')
            obj.set({ '@type': json_crdt_1.s.con('mutxt') });
        let peritextNode;
        let isNewDocument = false;
        try {
            peritextNode = obj.in(['text']).asExt(json_crdt_extensions_1.ext.peritext);
        }
        catch {
            isNewDocument = true;
            obj.set({ text: json_crdt_extensions_1.ext.peritext.new('') });
            peritextNode = obj.in(['text']).asExt(json_crdt_extensions_1.ext.peritext);
        }
        this.peritextRef = () => peritextNode;
        if (isNewDocument && opts?.fromSlate) {
            const viewRange = collaborative_slate_1.FromSlate.convert(normalizeDocument(opts.fromSlate));
            const txt = this.peritextRef().peritext();
            txt.editor.merge(viewRange);
            txt.refresh();
        }
        let initialValue;
        try {
            initialValue = (0, toSlate_1.toSlate)(this.peritextRef().peritext());
            if (!initialValue.length)
                initialValue = createEmptyDocument();
        }
        catch {
            initialValue = createEmptyDocument();
        }
        this.sizer = new Sizer_1.SizerState(Number(obj.read('/width')) || 1330);
        const storedFont = obj.read('/font');
        if ((0, font_1.isFontKind)(storedFont))
            this.font.next(storedFont);
        const storedEditableWidth = obj.read('/ew');
        if (isEditableWidth(storedEditableWidth))
            this.editableWidth.next(storedEditableWidth);
        const storedTheme = obj.read('/theme');
        if (isThemeOverride(storedTheme))
            this.theme.next(storedTheme);
        editor.children = initialValue;
        editor.selection = null;
        editor.onOpenInlineMathEdit = (element, path) => {
            this.inline.math.openEdit(element, path);
        };
    }
    start() {
        // -------------------------------------------------- Collaboration binding
        const facade = new collaborative_slate_1.SlateFacade(this.editor, this.peritextRef);
        const unbindCollaboration = PeritextBinding_1.PeritextBinding.bind(this.peritextRef, facade);
        queueMicrotask(() => this.sync(true));
        // -------------------------------------------------------------- Scrolling
        const scrollUnsubscribe = this.scroll.scrollTop$.subscribe(() => {
            this.scrollVersion.next(this.scrollVersion.value + 1);
        });
        // ------------------------------------------------ Sizer width persistence
        const stopSizerPersist = (0, watch_1.watch)(this.sizer.content, 600, 1, (w) => {
            if (w > 0 && !this.readOnly.value)
                this.obj.add('/width', Math.round(w));
        });
        const stopInline = this.inline.start();
        const stopBlock = this.block.start();
        const stopVoids = this.voids.start();
        const stopOmni = this.omni.start();
        const stopSelectAllGuard = this.selectAllGuard.start();
        const stopIndicator = this.indicator.start();
        const stopThings = this.things.start();
        const stopCustomStyle = this.customStyle.start();
        const stopTranslit = this.translit.start();
        const unbindShortcuts = (0, keyboard_2.bindShortcuts)(this);
        (0, imagePaste_1.bindImagePaste)(this);
        // --------------------------------------------- Native fullscreen tracking
        // Keep `displayMode` in sync when the user exits fullscreen via Esc or
        // when the browser drops out of fullscreen for any other reason.
        const onFullscreenChange = () => {
            const inNativeFullscreen = !!document.fullscreenElement;
            const mode = this.displayMode.value;
            if (!inNativeFullscreen && mode === 'fullscreen')
                this.displayMode.set('inline');
        };
        if (typeof document !== 'undefined')
            document.addEventListener('fullscreenchange', onFullscreenChange);
        // ------------------------------------------- System color-scheme tracking
        let systemDarkMq;
        let onSystemDarkChange;
        if (typeof window !== 'undefined' && window.matchMedia) {
            systemDarkMq = window.matchMedia('(prefers-color-scheme: dark)');
            this.systemDark.set(systemDarkMq.matches);
            onSystemDarkChange = (e) => this.systemDark.set(e.matches);
            systemDarkMq.addEventListener('change', onSystemDarkChange);
        }
        return () => {
            unbindCollaboration();
            scrollUnsubscribe();
            stopSizerPersist();
            stopInline();
            stopBlock();
            stopVoids();
            stopOmni();
            stopSelectAllGuard();
            stopIndicator();
            stopThings();
            stopCustomStyle();
            stopTranslit();
            unbindShortcuts();
            if (typeof document !== 'undefined')
                document.removeEventListener('fullscreenchange', onFullscreenChange);
            if (systemDarkMq && onSystemDarkChange)
                systemDarkMq.removeEventListener('change', onSystemDarkChange);
            this.kbdSourceUnbind?.();
            this.kbdSourceUnbind = undefined;
            this.kbd.dispose();
        };
    }
}
exports.MuTxtState = MuTxtState;
